import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Factory, Package, ChevronRight, ChevronLeft, Warehouse, Store, ArrowRight,
  Wheat, Boxes, CheckCircle2, Plus, ClipboardList, Truck, Cpu, Layers, FileText, TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ColorTab } from "@/components/ui/color-tab";
import { fmtDate, inr } from "@/lib/utils-format";
import { MfgMastersPanel } from "@/components/mfg/MfgMastersPanel";
import { MfgPlansTab } from "@/components/mfg/MfgPlansTab";
import { MfgOrdersTab } from "@/components/mfg/MfgOrdersTab";
import { MfgDispatchTab } from "@/components/mfg/MfgDispatchTab";
import { MfgBomTab } from "@/components/mfg/MfgBomTab";
import { CompleteBatchDialog, BatchProfitabilityPanel, InventoryLedgerPanel } from "@/pages/CostingPanels";

export default function ManufacturingPage() {
  return (
    <div className="space-y-6" data-testid="manufacturing-page">
      <div>
        <div className="uc-label">Production Operations</div>
        <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Manufacturing</h1>
        <p className="text-sm text-[#8B7355] mt-1">Plans, orders, batches, dispatch and masters — the full shop-floor loop in one place.</p>
      </div>

      <Tabs defaultValue="batches" className="space-y-4">
        <TabsList className="flex flex-wrap h-auto bg-transparent p-0 gap-0">
          <ColorTab value="batches" tone="orange" icon={Package} testid="mfg-tab-batches">Batches</ColorTab>
          <ColorTab value="plans" tone="blue" icon={ClipboardList} testid="mfg-tab-plans">Plans</ColorTab>
          <ColorTab value="orders" tone="purple" icon={Layers} testid="mfg-tab-orders">Orders</ColorTab>
          <ColorTab value="dispatch" tone="amber" icon={Truck} testid="mfg-tab-dispatch">Dispatch</ColorTab>
          <ColorTab value="bom" tone="purple" icon={FileText} testid="mfg-tab-bom">BOM</ColorTab>
          <ColorTab value="masters" tone="slate" icon={Cpu} testid="mfg-tab-masters">Masters</ColorTab>
          <ColorTab value="profitability" tone="green" icon={TrendingUp} testid="mfg-tab-profitability">Profitability</ColorTab>
          <ColorTab value="ledger" tone="blue" icon={Boxes} testid="mfg-tab-ledger">Inventory Ledger</ColorTab>
        </TabsList>

        <TabsContent value="batches"><BatchesTab /></TabsContent>
        <TabsContent value="plans"><MfgPlansTab /></TabsContent>
        <TabsContent value="orders"><MfgOrdersTab /></TabsContent>
        <TabsContent value="dispatch"><MfgDispatchTab /></TabsContent>
        <TabsContent value="bom"><MfgBomTab /></TabsContent>
        <TabsContent value="masters"><MfgMastersPanel /></TabsContent>
        <TabsContent value="profitability"><BatchProfitabilityPanel /></TabsContent>
        <TabsContent value="ledger"><InventoryLedgerPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

// ================= Batches tab (formerly the page body) =================
function BatchesTab() {
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [batches, setBatches] = useState([]);
  const [recipe, setRecipe] = useState(null);

  const [step, setStep] = useState(1);
  const [productId, setProductId] = useState("");
  const [quantity, setQuantity] = useState(10);
  const [batchNo, setBatchNo] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [notes, setNotes] = useState("");
  const [consumeIngredients, setConsumeIngredients] = useState(true);
  const [distributions, setDistributions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [lastBatch, setLastBatch] = useState(null);
  const [mfgDate, setMfgDate] = useState(new Date().toISOString().slice(0, 10));
  const [shelfLifeDays, setShelfLifeDays] = useState(7);
  const [recallDialog, setRecallDialog] = useState(null);
  const [completeDialog, setCompleteDialog] = useState(null);

  const loadAll = async () => {
    try {
      const [p, b, ba] = await Promise.all([
        api.get("/products"),
        api.get("/branches"),
        api.get("/production/batches"),
      ]);
      setProducts(p.data);
      setBranches(b.data);
      setBatches(ba.data);
      const warehouse = b.data.find((x) => x.type === "warehouse") || b.data.find((x) => x.type === "head_office");
      if (warehouse && !warehouseId) setWarehouseId(warehouse.id);
    } catch (e) {}
  };
  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    if (productId && consumeIngredients) {
      api.get(`/products/${productId}`).then((r) => setRecipe(r.data?.recipe || null)).catch(() => setRecipe(null));
    } else {
      setRecipe(null);
    }
  }, [productId, consumeIngredients]);

  const retailBranches = branches.filter((b) => b.type === "branch" && b.id !== warehouseId && b.is_active !== false);
  const product = products.find((p) => p.id === productId);

  const ingredientPreview = useMemo(() => {
    if (!recipe || !recipe.ingredients?.length || !quantity) return [];
    const yield_qty = Number(recipe.yield_qty || 1) || 1;
    const scale = Number(quantity) / yield_qty;
    return recipe.ingredients.map((i) => ({
      name: i.ingredient_name,
      quantity: (Number(i.quantity) * scale).toFixed(2),
      unit: i.unit,
      cost: (Number(i.cost_per_unit || 0) * Number(i.quantity) * scale).toFixed(2),
    }));
  }, [recipe, quantity]);

  const totalAllocated = distributions.reduce((s, d) => s + (Number(d.quantity) || 0), 0);
  const remainingToDistribute = Number(quantity) - totalAllocated;

  const openWizard = () => {
    setStep(1);
    setProductId("");
    setQuantity(10);
    setBatchNo("");
    setNotes("");
    setDistributions([]);
    setShowWizard(true);
  };

  const submit = async () => {
    if (!productId || !warehouseId || Number(quantity) <= 0) {
      return toast.error("Product, warehouse and quantity are required");
    }
    setBusy(true);
    try {
      const body = {
        product_id: productId,
        quantity: Number(quantity),
        batch_no: batchNo || undefined,
        warehouse_branch_id: warehouseId,
        consume_ingredients: consumeIngredients,
        notes: notes || undefined,
        mfg_date: mfgDate || undefined,
        shelf_life_days: Number(shelfLifeDays) > 0 ? Number(shelfLifeDays) : undefined,
        distributions: distributions.filter((d) => d.branch_id && Number(d.quantity) > 0).map((d) => ({ branch_id: d.branch_id, quantity: Number(d.quantity) })),
      };
      const { data } = await api.post("/production/batches", body);
      toast.success(`Batch ${data.batch_no} produced`);
      setLastBatch(data);
      setShowWizard(false);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const canProceedStep1 = productId && warehouseId && Number(quantity) > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end">
        <Button onClick={openWizard} data-testid="production-new-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
          <Plus className="w-4 h-4 mr-1.5" /> New production batch
        </Button>
      </div>

      {lastBatch && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3" data-testid="production-last-batch">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-emerald-900">Batch {lastBatch.batch_no} produced · {lastBatch.quantity} × {lastBatch.product_name}</div>
            <div className="text-sm text-emerald-800 mt-0.5">
              → Warehouse {lastBatch.warehouse_branch_name}
              {lastBatch.distributions?.length > 0 && <> · Distributed to <b>{lastBatch.distributions.length}</b> branch{lastBatch.distributions.length === 1 ? "" : "es"} ({lastBatch.allocated_qty} units)</>}
              {lastBatch.ingredient_cost > 0 && <> · Ingredient cost: {inr(lastBatch.ingredient_cost)}</>}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left uc-label border-b border-[#EAE5D9]">
              <th className="px-4 py-3">Batch #</th>
              <th className="px-4 py-3">Product</th>
              <th className="px-4 py-3 text-right">Qty</th>
              <th className="px-4 py-3">Warehouse</th>
              <th className="px-4 py-3">Distribution</th>
              <th className="px-4 py-3">Expiry</th>
              <th className="px-4 py-3 text-right">Cost/unit</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {batches.map((b) => (
              <tr key={b.id} className="border-b border-[#EAE5D9] last:border-0 hover:bg-[#FDFBF7]" data-testid={`batch-row-${b.id}`}>
                <td className="px-4 py-3 font-mono text-xs">{b.batch_no}</td>
                <td className="px-4 py-3 font-medium">{b.product_name} <span className="text-[10px] text-[#8B7355]">({b.product_sku})</span></td>
                <td className="px-4 py-3 text-right font-semibold">{b.quantity}</td>
                <td className="px-4 py-3">{b.warehouse_branch_name}</td>
                <td className="px-4 py-3">
                  {b.distributions?.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {b.distributions.map((d, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{d.branch_code}: {d.quantity}</Badge>
                      ))}
                      {b.unallocated_qty > 0 && <Badge variant="outline" className="text-xs bg-amber-50 border-amber-300 text-amber-700">Unallocated: {b.unallocated_qty}</Badge>}
                    </div>
                  ) : (
                    <span className="text-[#8B7355] text-xs">In warehouse</span>
                  )}
                </td>
                <td className="px-4 py-3 text-[#8B7355] text-xs">
                  {b.expiry_date ? (
                    <Badge variant="outline" className={
                      new Date(b.expiry_date) < new Date() ? "border-red-500 text-red-600" :
                      (new Date(b.expiry_date) - new Date()) / 86400000 <= 7 ? "border-amber-500 text-amber-600" :
                      "border-emerald-500 text-emerald-700"
                    }>{b.expiry_date}</Badge>
                  ) : "—"}
                </td>
                <td className="px-4 py-3 text-right text-xs">
                  {b.cost_per_unit ? (
                    <span title={`Total: ${b.total_cost}`}>{inr(b.cost_per_unit)}</span>
                  ) : (
                    <span className="text-[#8B7355]">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {b.status === "recalled" ? (
                    <Badge className="bg-red-600 text-white">Recalled</Badge>
                  ) : b.status === "completed" ? (
                    <Badge className="bg-emerald-600 text-white">Completed</Badge>
                  ) : (
                    <Badge variant="outline">{b.status || "active"}</Badge>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-3 items-center">
                    {(b.status === "active" || !b.status) && (
                      <button onClick={() => setCompleteDialog(b)} data-testid={`batch-complete-${b.id}`}
                              className="text-xs text-emerald-700 hover:underline">Mark Completed</button>
                    )}
                    {b.status !== "recalled" && (
                      <button onClick={() => setRecallDialog(b)} data-testid={`batch-recall-${b.id}`} className="text-xs text-red-600 hover:underline">Recall</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {batches.length === 0 && (
              <tr><td colSpan={9} className="text-center py-16 text-[#8B7355]"><Factory className="w-10 h-10 mx-auto mb-2 opacity-40" />No production batches yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showWizard && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowWizard(false)}>
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="production-wizard">
            <div className="px-6 py-4 border-b border-[#EAE5D9] flex items-center justify-between">
              <div>
                <div className="uc-label">Step {step} of 3</div>
                <div className="font-[Outfit] text-xl font-semibold">
                  {step === 1 && "Production entry"}
                  {step === 2 && "Recipe ingredients"}
                  {step === 3 && "Distribute to branches"}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {[1, 2, 3].map((n) => (
                  <div key={n} className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${step >= n ? "bg-[#C05621] text-white" : "bg-[#EAE5D9] text-[#8B7355]"}`}>{n}</div>
                ))}
              </div>
            </div>

            <div className="p-6 space-y-4">
              {step === 1 && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Product *</Label>
                    <Select value={productId} onValueChange={setProductId}>
                      <SelectTrigger data-testid="production-product-select"><SelectValue placeholder="Choose product…" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.sku}){p.has_recipe && " · recipe available"}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Quantity *</Label>
                    <Input type="number" min="0.01" step="0.5" data-testid="production-quantity" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
                  </div>
                  <div>
                    <Label>Batch # (auto if blank)</Label>
                    <Input value={batchNo} onChange={(e) => setBatchNo(e.target.value)} placeholder="e.g. BB-14-DEC" />
                  </div>
                  <div>
                    <Label>Manufacturing date</Label>
                    <Input type="date" value={mfgDate} onChange={(e) => setMfgDate(e.target.value)} data-testid="production-mfg-date" />
                  </div>
                  <div>
                    <Label>Shelf life (days)</Label>
                    <Input type="number" min="0" value={shelfLifeDays} onChange={(e) => setShelfLifeDays(e.target.value)} data-testid="production-shelf-life" placeholder="7 days = auto expiry" />
                  </div>
                  <div className="col-span-2">
                    <Label>Warehouse *</Label>
                    <Select value={warehouseId} onValueChange={setWarehouseId}>
                      <SelectTrigger data-testid="production-warehouse-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {branches.filter((b) => ["warehouse", "head_office"].includes(b.type)).map((b) => (
                          <SelectItem key={b.id} value={b.id}>{b.name} ({b.code}) · {b.type.replace("_", " ")}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label>Notes</Label>
                    <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional QC notes / batch conditions" />
                  </div>
                  <label className="col-span-2 flex items-center gap-3 p-3 bg-[#F7F2E8] rounded-md cursor-pointer">
                    <Switch checked={consumeIngredients} onCheckedChange={setConsumeIngredients} data-testid="production-consume-ingredients" />
                    <div>
                      <div className="font-medium text-sm">Auto-consume recipe ingredients</div>
                      <div className="text-xs text-[#8B7355]">Deducts scaled quantities from ingredient stock using the product recipe.</div>
                    </div>
                  </label>
                </div>
              )}

              {step === 2 && (
                <>
                  <div className="flex items-center gap-2 text-sm">
                    <Wheat className="w-4 h-4 text-[#C05621]" />
                    <span className="uc-label !text-[#5C4033]">Preview — will be deducted on save</span>
                  </div>
                  {!consumeIngredients ? (
                    <div className="p-6 text-center text-[#8B7355] text-sm bg-[#FEF9EA] border border-amber-200 rounded-md">
                      Ingredient consumption is OFF. Skipping this step — only finished goods will be added to warehouse.
                    </div>
                  ) : ingredientPreview.length === 0 ? (
                    <div className="p-6 text-center text-[#8B7355] text-sm bg-[#FEF9EA] border border-amber-200 rounded-md">
                      No recipe found for <b>{product?.name}</b>. Set one up under Recipes & Costing first, or continue without ingredient deduction.
                    </div>
                  ) : (
                    <div className="border border-[#EAE5D9] rounded-md overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-[#F7F2E8] uc-label">
                          <tr>
                            <th className="text-left px-4 py-2">Ingredient</th>
                            <th className="text-right px-4 py-2">Quantity</th>
                            <th className="text-right px-4 py-2">Line cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ingredientPreview.map((i, idx) => (
                            <tr key={idx} className="border-t border-[#EAE5D9]" data-testid={`ing-preview-${idx}`}>
                              <td className="px-4 py-2">{i.name}</td>
                              <td className="px-4 py-2 text-right">{i.quantity} {i.unit}</td>
                              <td className="px-4 py-2 text-right">{inr(i.cost)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="bg-[#F7F2E8] font-semibold">
                            <td colSpan={2} className="px-4 py-2 text-right">Total ingredient cost</td>
                            <td className="px-4 py-2 text-right">{inr(ingredientPreview.reduce((s, i) => s + Number(i.cost), 0))}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </>
              )}

              {step === 3 && (
                <>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                      <Warehouse className="w-4 h-4 text-[#C05621]" />
                      <span className="uc-label !text-[#5C4033]">Split into branches (optional)</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-[#8B7355]">Remaining in warehouse:</span> <b className={remainingToDistribute < 0 ? "text-red-500" : ""}>{remainingToDistribute}</b> / {quantity}
                    </div>
                  </div>
                  {retailBranches.length === 0 ? (
                    <div className="p-6 text-center text-[#8B7355] text-sm bg-[#FEF9EA] border border-amber-200 rounded-md">
                      No retail branches to distribute to. All produced goods will stay in warehouse.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {retailBranches.map((b) => {
                        const row = distributions.find((d) => d.branch_id === b.id);
                        return (
                          <div key={b.id} className="flex items-center gap-3 p-3 border border-[#EAE5D9] rounded-md" data-testid={`distribute-${b.id}`}>
                            <div className="w-9 h-9 rounded-md bg-[#FEEBC8] text-[#C05621] flex items-center justify-center">
                              <Store className="w-4 h-4" />
                            </div>
                            <div className="flex-1">
                              <div className="font-medium text-sm">{b.name}</div>
                              <div className="text-[10px] uppercase tracking-widest text-[#8B7355]">{b.code} · {b.city || "—"}</div>
                            </div>
                            <ArrowRight className="w-4 h-4 text-[#8B7355]" />
                            <Input
                              type="number"
                              min="0"
                              step="0.5"
                              className="w-28"
                              value={row?.quantity || ""}
                              placeholder="0"
                              onChange={(e) => {
                                const val = e.target.value;
                                setDistributions((prev) => {
                                  const rest = prev.filter((d) => d.branch_id !== b.id);
                                  return val ? [...rest, { branch_id: b.id, quantity: Number(val) }] : rest;
                                });
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {remainingToDistribute < 0 && (
                    <div className="text-sm text-red-600">You've allocated more than you're producing. Reduce a distribution above.</div>
                  )}
                </>
              )}
            </div>

            <div className="px-6 py-4 border-t border-[#EAE5D9] flex items-center justify-between bg-[#FDFBF7]">
              <Button variant="outline" onClick={() => setShowWizard(false)}>Cancel</Button>
              <div className="flex items-center gap-2">
                {step > 1 && (
                  <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="production-back">
                    <ChevronLeft className="w-4 h-4 mr-1" /> Back
                  </Button>
                )}
                {step < 3 ? (
                  <Button
                    onClick={() => setStep(step + 1)}
                    disabled={step === 1 && !canProceedStep1}
                    data-testid="production-next"
                    className="bg-[#C05621] hover:bg-[#9C4221] text-white"
                  >
                    Next <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (
                  <Button
                    onClick={submit}
                    disabled={busy || remainingToDistribute < 0}
                    data-testid="production-submit"
                    className="bg-[#C05621] hover:bg-[#9C4221] text-white"
                  >
                    {busy ? "Producing…" : "Produce & distribute"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {recallDialog && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setRecallDialog(null)}>
          <div className="bg-white rounded-lg max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="batch-recall-dialog">
            <div className="uc-label text-red-600 mb-1">⚠ Recall batch</div>
            <div className="font-[Outfit] text-xl font-semibold mb-1">{recallDialog.batch_no}</div>
            <div className="text-sm text-[#5C4033] mb-4">{recallDialog.product_name} · {recallDialog.quantity} units</div>
            <Label>Reason (required)</Label>
            <textarea
              className="w-full mt-1 h-24 border border-[#EAE5D9] rounded-md p-2 text-sm outline-none focus:border-red-500 resize-none"
              placeholder="e.g. QC failed — batch contamination detected"
              value={recallDialog.reason || ""}
              onChange={(e) => setRecallDialog({ ...recallDialog, reason: e.target.value })}
              data-testid="recall-reason"
            />
            <div className="text-xs text-[#8B7355] mt-2">This will block all POS sales of this batch across every branch and log a recall notification.</div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setRecallDialog(null)}>Cancel</Button>
              <Button
                onClick={async () => {
                  if (!recallDialog.reason?.trim()) return toast.error("Reason is required");
                  try {
                    await api.post(`/production/batches/${recallDialog.id}/recall`, { reason: recallDialog.reason, alert_branches: true });
                    toast.success(`Batch ${recallDialog.batch_no} recalled`);
                    setRecallDialog(null);
                    loadAll();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Failed");
                  }
                }}
                data-testid="recall-submit"
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Confirm recall
              </Button>
            </div>
          </div>
        </div>
      )}

      <CompleteBatchDialog
        batch={completeDialog}
        open={!!completeDialog}
        onOpenChange={(o) => !o && setCompleteDialog(null)}
        onCompleted={() => { setCompleteDialog(null); loadAll(); }}
      />
    </div>
  );
}
